export type AllManualActionsPanelFilters = {
  action_type: string | null;
  agent: string;
  role: string | null;
  section: string | null;
  source_ip: string;
  username: string;
};

export type StringSelectOption = {
  value: string;
  label: string;
};

/** @deprecated Prefer `StringSelectOption` — kept for existing imports. */
export type SectionSelectOption = StringSelectOption;

/**
 * Section dropdown options from All Events `meta.filters` (`event_category`).
 * Empty selection means "all".
 */
export function buildSectionOptions(
  categories: readonly StringSelectOption[],
  selectedSection?: string | null,
): StringSelectOption[] {
  const byValue = new Map<string, StringSelectOption>();
  for (const option of categories) {
    if (!option?.value) continue;
    byValue.set(option.value, option);
  }
  if (selectedSection && !byValue.has(selectedSection)) {
    byValue.set(selectedSection, {
      value: selectedSection,
      label: selectedSection,
    });
  }
  return Array.from(byValue.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * Action Type dropdown options from All Events `meta.filters` (`action_type`),
 * optionally narrowed by `cascading_options[section]`.
 */
export function buildActionTypeOptions(
  actionTypes: readonly StringSelectOption[],
  selectedActionType?: string | null,
  allowedValues?: readonly string[] | null,
): StringSelectOption[] {
  const allowed = allowedValues?.length ? new Set(allowedValues) : null;
  const byValue = new Map<string, StringSelectOption>();
  for (const option of actionTypes) {
    if (!option?.value) continue;
    if (allowed && !allowed.has(option.value)) continue;
    byValue.set(option.value, option);
  }
  if (selectedActionType && !byValue.has(selectedActionType)) {
    byValue.set(selectedActionType, {
      value: selectedActionType,
      label: selectedActionType,
    });
  }
  return Array.from(byValue.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

type WatchedPanelFields = {
  action_type: string | null | undefined;
  role: string | null | undefined;
  section: string | null | undefined;
  source_ip: string | null | undefined;
  username: string | null | undefined;
};

export function isAllManualActionsPanelDirty(
  watched: WatchedPanelFields,
  applied: AllManualActionsPanelFilters,
  isAgentDirty: boolean,
): boolean {
  return (
    (watched.action_type ?? null) !== applied.action_type ||
    isAgentDirty ||
    (watched.role ?? null) !== applied.role ||
    (watched.section ?? null) !== applied.section ||
    (watched.source_ip ?? "") !== applied.source_ip ||
    (watched.username ?? "") !== applied.username
  );
}

export function hasAppliedAllManualActionsFilters(
  applied: AllManualActionsPanelFilters,
): boolean {
  return (
    applied.action_type != null ||
    applied.agent !== "" ||
    applied.role != null ||
    applied.section != null ||
    applied.source_ip !== "" ||
    applied.username !== ""
  );
}
